import React, { useCallback, useRef, useMemo, useEffect, useState } from 'react'
import { Formik, useFormikContext } from 'formik'
import * as Yup from 'yup'
import get from 'lodash.get'
import format from 'date-fns/format'
import { cloneDeep, isEqual } from 'lodash'
import parse from 'date-fns/parse'

import { MobileDatePicker } from '@mui/lab'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import MenuItem from '@mui/material/MenuItem'

import Modal from 'components/common/Modal'

import useNotification from 'hooks/context/useNotification'

import {
  AGENCY_MAILING_ADDRESS_MEDIA_TYPES,
  DAYFORCE_AGENCY_PREFERENCES_ATTACH_ORDER,
  DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPE,
  DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPES,
  DAYFORCE_AGENCY_PREFERENCES_DOCUMENTS_SORT_BY,
  DAYFORCE_AGENCY_PREFERENCES_DUE_DATE_FREQUENCIES,
  DAYFORCE_AGENCY_PREFERENCES_DUE_DATE_FREQUENCY,
  DAYFORCE_AGENCY_PREFERENCES_DUE_DATE_TYPES,
  DAYFORCE_AGENCY_PREFERENCES_EFILE_METHODS,
  DAYFORCE_AGENCY_PREFERENCES_JURISDICTION_TYPES,
  DAYFORCE_AGENCY_PREFERENCES_SORT_ORDER,
} from 'utils/constants'
import getFrequencyDisplay from 'helpers/datetime/getFrequencyDisplay'
import axios from 'utils/axios'
import DailyMonthlyCnCForm from './DailyMonthlyCnCForm'
import QuarterEndReturns from './QuarterEndReturns'
import mergeDeep from 'helpers/node/mergeDeep'
import useConfiguration from 'hooks/useConfiguration'
import useAuth from 'hooks/useAuth'
import set from 'lodash.set'
import hasTrue from 'helpers/node/hasTrue'
import isEmptyObject from 'helpers/node/isEmptyObject'
import deepEqual from 'deep-equal'
import { generateAddressModalProps } from 'utils/smartyStreets'
import useModal from 'hooks/context/useModal'

const sharedValidationSchema = {
  tax_code: Yup.string()
    .required('Tax code is required')
    .matches(/^[a-zA-Z0-9]+$/, 'Tax code must be alphanumeric'),
  tax_code_description: Yup.string().required(
    'Tax code description is required'
  ),
  jurisdiction_type: Yup.string()
    .required('Jurisdiction type is required')
    .oneOf(
      Object.keys(DAYFORCE_AGENCY_PREFERENCES_JURISDICTION_TYPES),
      `Jurisdiction type must be one of (${Object.keys(
        DAYFORCE_AGENCY_PREFERENCES_JURISDICTION_TYPES
      ).join(', ')})`
    ),
  document_type: Yup.string()
    .required('Document type is required')
    .oneOf(
      Object.keys(DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPES),
      'Document type must be one of the following: ' +
        Object.values(DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPES).join(', ')
    ),
}

const sharedInitialValues = {
  tax_code: '',
  tax_code_description: '',
  jurisdiction_type: '',
  document_type: '',
  mailing_preferences: {},
}

const documentTypeInitialValues = {
  [DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPE.DAILY_OR_MONTHLY_CHECKS_AND_COUPONS]:
    {
      ...sharedInitialValues,
      mailing_preferences: {
        primary_mailing_address: {
          recipient_line_1: '',
          recipient_line_2: '',
          state: '',
          city: '',
          address1: '',
          address2: '',
          zip: '',
          country: 'USA',
        },
        overnight_mailing_address: {
          recipient_line_1: '',
          recipient_line_2: '',
          state: '',
          city: '',
          address1: '',
          address2: '',
          zip: '',
          country: 'USA',
        },
        alternate_mailing_address: {
          recipient_line_1: '',
          recipient_line_2: '',
          state: '',
          city: '',
          address1: '',
          address2: '',
          zip: '',
          country: 'USA',
        },
        zero_coupons: {
          send: false,
        },
        applied_fors: {
          send: false,
          mailing_address: '',
          mail_with_checks: true,
        },
        reports: {
          generate_reports: false,
          mailing_address: '',
          mail_with_checks: true,
        },
        cd: {
          generate_cd: false,
          mailing_address: '',
          mail_with_checks: true,
        },
        special_handling: {
          required: false,
          instructions: '',
        },
        agency_package: {
          hold_checks_to_due_date: true,
          sort_order: 'clientid_dollar_coupon_check',
        },
        additional_requirements: '',
        always_ship_overnight: false,
        due_date: {
          frequency: '',
          start_date: new Date(),
        },
      },
      includeAlternateMailingAddress: false,
      includeOvernightMailingAddress: false,
    },
  [DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPE.QUARTER_END_RETURNS]: {
    ...sharedInitialValues,
    mailing_preferences: {
      due_date: {
        frequency: '',
        start_date: new Date(),
        type: '',
      },
      agency_package: {
        return: {
          valid_ids: {
            file_electronically: false,
            efile_method: '',
            file_paper: false,
            file_on_cd: false,
            include_zeros: false,
            include_dollars: false,
          },
          applied_fors: {
            file_electronically: false,
            efile_method: '',
            file_paper: false,
            file_on_cd: false,
            include_zeros: false,
            include_dollars: false,
          },
        },
        wage_detail: {
          valid_ids: {
            file_electronically: false,
            efile_method: '',
            file_paper: false,
            file_on_cd: false,
            include_zeros: false,
            include_dollars: false,
          },
          applied_fors: {
            file_electronically: false,
            efile_method: '',
            file_paper: false,
            file_on_cd: false,
            include_zeros: false,
            include_dollars: false,
          },
        },
        payment_types: {
          valid_ids: {
            eft_ach: false,
            individual_check: false,
            batch_check: false,
            payment_voucher: false,
          },
          applied_fors: {
            eft_ach: false,
            individual_check: false,
            batch_check: false,
            payment_voucher: false,
          },
        },
        other_requirements: {
          cover_letter: false,
          other_documents: false,
          client_listing_required: false,
          out_of_balance_accepted: false,
        },
        mailing_information: {
          address1: {
            media_type: '',
            mail_method: '',
            attach_order: '',
            sort_documents_sort_by: '',
            title: '',
            department: '',
            address_line_1: '',
            address_line_2: '',
            city: '',
            state: '',
            zip: '',
            country: 'USA',
          },
          address2: {
            media_type: '',
            mail_method: '',
            attach_order: '',
            sort_documents_sort_by: '',
            title: '',
            department: '',
            address_line_1: '',
            address_line_2: '',
            city: '',
            state: '',
            zip: '',
            country: 'USA',
          },
          address3: {
            media_type: '',
            mail_method: '',
            attach_order: '',
            sort_documents_sort_by: '',
            title: '',
            department: '',
            address_line_1: '',
            address_line_2: '',
            city: '',
            state: '',
            zip: '',
            country: 'USA',
          },
          address4: {
            media_type: '',
            mail_method: '',
            attach_order: '',
            sort_documents_sort_by: '',
            title: '',
            department: '',
            address_line_1: '',
            address_line_2: '',
            city: '',
            state: '',
            zip: '',
            country: 'USA',
          },
        },
      },
      additional_requirements: '',
    },
    includeAgencyPackageReturn: false,
    includeAgencyPackageWageDetail: false,
    includeAgencyPackagePaymentTypes: false,
    includeAgencyPackageOtherRequirements: false,
    includeAddress1: false,
    includeAddress2: false,
    includeAddress3: false,
    includeAddress4: false,
    shouldSortDocumentsAddress1: false,
    shouldSortDocumentsAddress2: false,
    shouldSortDocumentsAddress3: false,
    shouldSortDocumentsAddress4: false,
  },
}

const forms = {
  [DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPE.DAILY_OR_MONTHLY_CHECKS_AND_COUPONS]:
    <DailyMonthlyCnCForm />,
  [DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPE.QUARTER_END_RETURNS]: (
    <QuarterEndReturns />
  ),
}

const DueDateSelect = ({ disabled = false }) => {
  const { values, handleChange, errors, touched } = useFormikContext()
  const isQuarterEndReturns =
    values?.document_type ===
    DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPE.QUARTER_END_RETURNS

  return (
    <>
      {isQuarterEndReturns && (
        <TextField
          required
          value={values?.mailing_preferences?.due_date?.type || ''}
          error={Boolean(errors?.mailing_preferences?.due_date?.type)}
          helperText={errors?.mailing_preferences?.due_date?.type}
          onChange={handleChange}
          name="mailing_preferences.due_date.type"
          label="Due Date Type"
          select
          fullWidth
          size="small"
          variant="outlined"
          disabled={disabled}
        >
          {Object.entries(DAYFORCE_AGENCY_PREFERENCES_DUE_DATE_TYPES).map(
            ([value, display]) => (
              <MenuItem key={value} value={value}>
                {display}
              </MenuItem>
            )
          )}
        </TextField>
      )}
      <TextField
        required
        value={values?.mailing_preferences?.due_date?.frequency || ''}
        error={Boolean(errors?.mailing_preferences?.due_date?.frequency)}
        helperText={errors?.mailing_preferences?.due_date?.frequency}
        onChange={handleChange}
        name="mailing_preferences.due_date.frequency"
        label="Frequency"
        select
        fullWidth
        size="small"
        variant="outlined"
        disabled={disabled}
      >
        {Object.entries(DAYFORCE_AGENCY_PREFERENCES_DUE_DATE_FREQUENCIES).map(
          ([value, display]) => (
            <MenuItem key={value} value={value}>
              {display}
            </MenuItem>
          )
        )}
      </TextField>
      {values?.mailing_preferences?.due_date?.frequency &&
        values?.mailing_preferences?.due_date?.frequency !==
          DAYFORCE_AGENCY_PREFERENCES_DUE_DATE_FREQUENCY.DAILY && (
          <MobileDatePicker
            value={
              values?.mailing_preferences?.due_date?.start_date || new Date()
            }
            onChange={(value) => {
              handleChange({
                target: {
                  name: 'mailing_preferences.due_date.start_date',
                  value,
                },
              })
            }}
            name="mailing_preferences.due_date.start_date"
            inputFormat="MM/dd/yyyy"
            showDaysOutsideCurrentMonth={false}
            renderInput={(params) => (
              <TextField
                {...params}
                inputProps={{
                  ...params.inputProps,
                  value: getFrequencyDisplay(
                    values?.mailing_preferences?.due_date?.start_date,
                    values?.mailing_preferences?.due_date?.frequency
                  ),
                  readOnly: true,
                }}
                error={Boolean(
                  get(touched, 'mailing_preferences.due_date.start_date') &&
                    get(errors, 'mailing_preferences.due_date.start_date')
                )}
                helperText={
                  get(touched, 'mailing_preferences.due_date.start_date') &&
                  get(errors, 'mailing_preferences.due_date.start_date')
                }
                required
                label="Due Date"
                fullWidth
                size="small"
                variant="outlined"
                disabled={disabled}
              />
            )}
          />
        )}
    </>
  )
}

const normalizeTaxCode = (taxCode) => {
  if (!taxCode) return ''
  // Trim whitespace and remove leading zeros, but preserve at least one digit if the code is all zeros
  const trimmed = taxCode.toString().trim()
  const normalized = trimmed.replace(/^0+/, '') || '0'
  return normalized
}

const getTaxCodePreferences = async (taxCode) => {
  try {
    const res = await axios.get(`/agency-mailing?search=tax_code|${taxCode}`)
    const normalizedInput = normalizeTaxCode(taxCode)
    return res.data._embedded.agency_preferences.filter(
      (p) => normalizeTaxCode(p.tax_code) === normalizedInput
    )
  } catch (err) {
    return []
  }
}

const TaxCodeInput = ({ isAddModal }) => {
  const {
    values,
    handleChange,
    errors,
    touched,
    setFieldError,
    handleBlur,
    setStatus,
    status,
  } = useFormikContext()

  const handleTaxCodeChange = (e) => {
    handleChange(e)
    setFieldError('tax_code', undefined)
  }

  const handleTaxCodeBlur = (e) => {
    handleBlur(e)

    const taxCode = e.target.value

    setStatus({
      ...status,
      existingDocumentTypes: [],
    })

    if (taxCode) {
      getTaxCodePreferences(taxCode?.trim()).then((preferences) => {
        const hasInactive = preferences.some((p) => p.is_active === false)
        const inactiveMessage = hasInactive
          ? ' One or more agency preferences may be inactive.'
          : ''

        if (
          values?.document_type &&
          preferences.find((p) => p.document_type === values?.document_type)
        ) {
          setFieldError(
            'tax_code',
            `Agency Preference already exists for this tax code and document type.${inactiveMessage}`
          )
          setFieldError(
            'document_type',
            `Agency Preference already exists for this tax code and document type.${inactiveMessage}`
          )
        }

        if (
          preferences.length ===
          Object.keys(DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPES).length
        ) {
          setFieldError(
            'tax_code',
            `All document types already have an agency preference for this tax code.${inactiveMessage}`
          )
        }

        setStatus({
          ...status,
          existingDocumentTypes: preferences.map((p) => p.document_type),
        })
      })
    }
  }

  if (!isAddModal) return <></>

  return (
    <TextField
      required
      fullWidth
      size="small"
      label="Tax Code"
      value={values?.tax_code?.trim()}
      error={Boolean(get(touched, 'tax_code') && get(errors, 'tax_code'))}
      helperText={get(touched, 'tax_code') && get(errors, 'tax_code')}
      onChange={handleTaxCodeChange}
      onBlur={handleTaxCodeBlur}
      name="tax_code"
      variant="outlined"
      disabled={!isAddModal}
    />
  )
}

const DocumentTypeSelect = ({
  isAddModal,
  setDocumentType,
  formDisabled,
  setPreservedFields,
}) => {
  const {
    values,
    handleChange,
    errors,
    touched,
    handleBlur,
    setFieldError,
    status,
    setStatus,
  } = useFormikContext()
  const isDocumentTypeSelected = Boolean(values?.document_type)

  const handleDocumentTypeBlur = (e) => {
    handleBlur(e)

    if (isAddModal && e.target.value) {
      getTaxCodePreferences(values?.tax_code).then((preferences) => {
        if (preferences.find((p) => p.document_type === e.target.value)) {
          setFieldError(
            'tax_code',
            'Agency Preference already exists for this tax code and document type'
          )
          setFieldError(
            'document_type',
            'Agency Preference already exists for this tax code and document type'
          )
        }

        if (
          preferences.length ===
          Object.keys(DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPES).length
        ) {
          setFieldError(
            'tax_code',
            'All document types already have an agency preference for this tax code'
          )
        }

        setStatus({
          ...status,
          existingDocumentTypes: preferences.map((p) => p.document_type),
        })
      })
    }
  }

  if (!isAddModal && !isDocumentTypeSelected) return <></>

  return (
    <TextField
      required
      value={values?.document_type || ''}
      onChange={(e) => {
        const newDocumentType = e.target.value
        // Preserve tax_code, tax_code_description, and jurisdiction_type in state
        // This will be included in initialValues on next render, preventing flash
        if (isAddModal) {
          setPreservedFields({
            tax_code: values?.tax_code || '',
            tax_code_description: values?.tax_code_description || '',
            jurisdiction_type: values?.jurisdiction_type || '',
          })
        }
        handleChange(e)
        setDocumentType(newDocumentType)
      }}
      onBlur={handleDocumentTypeBlur}
      name="document_type"
      label="Document Type"
      InputLabelProps={{
        shrink: true,
      }}
      SelectProps={{
        displayEmpty: true,
      }}
      InputProps={{
        sx: !isDocumentTypeSelected ? { color: 'darkgray.light' } : undefined,
      }}
      select
      fullWidth
      size="small"
      error={Boolean(
        get(touched, 'document_type') && get(errors, 'document_type')
      )}
      helperText={get(touched, 'document_type') && get(errors, 'document_type')}
      variant="outlined"
      disabled={formDisabled}
    >
      <MenuItem sx={{ display: 'none' }} value="" disabled>
        Selection Required
      </MenuItem>
      {Object.keys(DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPES).map((type) => (
        <MenuItem
          key={type}
          value={type}
          disabled={status?.existingDocumentTypes?.includes(type)}
        >
          {DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPES[type]}
        </MenuItem>
      ))}
    </TextField>
  )
}

function DayforceEditPreferencesModal({
  open,
  setOpen,
  agency,
  addAgency,
  updateAgencyPreferences,
}) {
  const { setBasicNotification, setError } = useNotification()
  const formikRef = useRef()
  const [documentType, setDocumentType] = useState(agency?.document_type || '')
  const [preservedFields, setPreservedFields] = useState({
    tax_code: '',
    tax_code_description: '',
    jurisdiction_type: '',
  })
  const { instanceConfigurations } = useConfiguration()
  const { blockIdleTimer } = useAuth()
  const mediaTypes = useMemo(
    () => instanceConfigurations?.[AGENCY_MAILING_ADDRESS_MEDIA_TYPES] || [],
    [instanceConfigurations]
  )
  const {
    setModalProps,
    setComponent: setModalComponent,
    setOpen: setModalOpen,
  } = useModal()

  const documentTypeValidationSchema = useMemo(
    () => ({
      [DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPE.DAILY_OR_MONTHLY_CHECKS_AND_COUPONS]:
        {
          ...sharedValidationSchema,
          mailing_preferences: Yup.object().shape({
            due_date: Yup.object().shape({
              frequency: Yup.string()
                .required('Frequency is required')
                .oneOf(
                  Object.keys(DAYFORCE_AGENCY_PREFERENCES_DUE_DATE_FREQUENCIES),
                  'Frequency must be one of the following: ' +
                    Object.keys(
                      DAYFORCE_AGENCY_PREFERENCES_DUE_DATE_FREQUENCIES
                    ).join(', ')
                ),
              start_date: Yup.date()
                .nullable()
                .when('frequency', {
                  is: (val) =>
                    val &&
                    val !==
                      DAYFORCE_AGENCY_PREFERENCES_DUE_DATE_FREQUENCY.DAILY,
                  then: (schema) => schema.required('Due date is required'),
                }),
            }),
            primary_mailing_address: Yup.object().shape({
              recipient_line_1: Yup.string().required(
                'Recipient line 1 is required'
              ),
              address1: Yup.string().required('Address is required'),
              city: Yup.string().required('City is required'),
              state: Yup.string().required('State is required'),
              zip: Yup.string().required('Zip code is required'),
              country: Yup.string().required('Country is required'),
            }),
            overnight_mailing_address: Yup.object().nullable(),
            alternate_mailing_address: Yup.object().nullable(),
            applied_fors: Yup.object().shape({
              send: Yup.boolean(),
              mail_with_checks: Yup.boolean(),
              mailing_address: Yup.string()
                .nullable()
                .oneOf(
                  [
                    '',
                    'primary_mailing_address',
                    'overnight_mailing_address',
                    'alternate_mailing_address',
                  ],
                  'Invalid mailing address selection'
                ),
            }),
            reports: Yup.object().shape({
              generate_reports: Yup.boolean(),
              mail_with_checks: Yup.boolean(),
              mailing_address: Yup.string()
                .nullable()
                .oneOf(
                  [
                    '',
                    'primary_mailing_address',
                    'overnight_mailing_address',
                    'alternate_mailing_address',
                  ],
                  'Invalid mailing address selection'
                ),
            }),
            cd: Yup.object().shape({
              generate_cd: Yup.boolean(),
              mail_with_checks: Yup.boolean(),
              mailing_address: Yup.string()
                .nullable()
                .oneOf(
                  [
                    '',
                    'primary_mailing_address',
                    'overnight_mailing_address',
                    'alternate_mailing_address',
                  ],
                  'Invalid mailing address selection'
                ),
            }),
            special_handling: Yup.object().shape({
              required: Yup.boolean(),
              instructions: Yup.string().nullable(),
            }),
            agency_package: Yup.object().shape({
              sort_order: Yup.string()
                .required('Sort order is required')
                .oneOf(
                  Object.keys(DAYFORCE_AGENCY_PREFERENCES_SORT_ORDER),
                  'Invalid sort order selection'
                ),
            }),
            additional_requirements: Yup.string(),
            zero_coupons: Yup.object().shape({
              send: Yup.boolean(),
            }),
          }),
        },
      [DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPE.QUARTER_END_RETURNS]: {
        ...sharedValidationSchema,
        mailing_preferences: Yup.object().shape({
          due_date: Yup.object().shape({
            frequency: Yup.string()
              .required('Frequency is required')
              .oneOf(
                Object.keys(DAYFORCE_AGENCY_PREFERENCES_DUE_DATE_FREQUENCIES),
                'Frequency must be one of the following: ' +
                  Object.keys(
                    DAYFORCE_AGENCY_PREFERENCES_DUE_DATE_FREQUENCIES
                  ).join(', ')
              ),
            start_date: Yup.date()
              .nullable()
              .when('frequency', {
                is: (val) =>
                  val &&
                  val !== DAYFORCE_AGENCY_PREFERENCES_DUE_DATE_FREQUENCY.DAILY,
                then: (schema) => schema.required('Due date is required'),
              }),
            type: Yup.string()
              .nullable()
              .oneOf(
                Object.keys(DAYFORCE_AGENCY_PREFERENCES_DUE_DATE_TYPES),
                'Invalid due date type selection'
              )
              .required('Due date type is required'),
          }),
          agency_package: Yup.object().shape({
            return: Yup.object().shape({
              valid_ids: Yup.object().shape({
                file_electronically: Yup.boolean().nullable(),
                efile_method: Yup.string()
                  .nullable()
                  .oneOf(
                    Object.keys(DAYFORCE_AGENCY_PREFERENCES_EFILE_METHODS),
                    'Invalid e-file method selection'
                  ),
                file_paper: Yup.boolean().nullable(),
                file_on_cd: Yup.boolean().nullable(),
                include_zeros: Yup.boolean().nullable(),
                include_dollars: Yup.boolean().nullable(),
              }),
              applied_fors: Yup.object().shape({
                file_electronically: Yup.boolean().nullable(),
                efile_method: Yup.string()
                  .nullable()
                  .oneOf(
                    Object.keys(DAYFORCE_AGENCY_PREFERENCES_EFILE_METHODS),
                    'Invalid e-file method selection'
                  ),
                file_paper: Yup.boolean().nullable(),
                file_on_cd: Yup.boolean().nullable(),
                include_zeros: Yup.boolean().nullable(),
                include_dollars: Yup.boolean().nullable(),
              }),
            }),
            wage_detail: Yup.object().shape({
              valid_ids: Yup.object().shape({
                file_electronically: Yup.boolean().nullable(),
                efile_method: Yup.string()
                  .nullable()
                  .oneOf(
                    Object.keys(DAYFORCE_AGENCY_PREFERENCES_EFILE_METHODS),
                    'Invalid e-file method selection'
                  ),
                file_paper: Yup.boolean().nullable(),
                file_on_cd: Yup.boolean().nullable(),
                include_zeros: Yup.boolean().nullable(),
                include_dollars: Yup.boolean().nullable(),
              }),
              applied_fors: Yup.object().shape({
                file_electronically: Yup.boolean().nullable(),
                efile_method: Yup.string()
                  .nullable()
                  .oneOf(
                    Object.keys(DAYFORCE_AGENCY_PREFERENCES_EFILE_METHODS),
                    'Invalid e-file method selection'
                  ),
                file_paper: Yup.boolean().nullable(),
                file_on_cd: Yup.boolean().nullable(),
                include_zeros: Yup.boolean().nullable(),
                include_dollars: Yup.boolean().nullable(),
              }),
            }),
            payment_types: Yup.object().shape({
              valid_ids: Yup.object().shape({
                eft_ach: Yup.boolean().nullable(),
                individual_check: Yup.boolean().nullable(),
                batch_check: Yup.boolean().nullable(),
                payment_voucher: Yup.boolean().nullable(),
              }),
              applied_fors: Yup.object().shape({
                eft_ach: Yup.boolean().nullable(),
                individual_check: Yup.boolean().nullable(),
                batch_check: Yup.boolean().nullable(),
                payment_voucher: Yup.boolean().nullable(),
              }),
            }),
            other_requirements: Yup.object().shape({
              cover_letter: Yup.boolean().nullable(),
              other_documents: Yup.boolean().nullable(),
              client_listing_required: Yup.boolean().nullable(),
              out_of_balance_accepted: Yup.boolean().nullable(),
            }),
          }),
          additional_requirements: Yup.string(),
        }),
      },
    }),
    [mediaTypes]
  )

  const isAddModal = !Boolean(agency)
  const documentTypeDisplay =
    DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPES[agency?.document_type]

  const initialValues = useMemo(() => {
    const getStartDate = (date) => {
      if (!date) return new Date()

      try {
        if (date instanceof Date) return date

        return parse(date, 'yyyy-MM-dd', new Date())
      } catch (err) {
        return new Date()
      }
    }

    let values = cloneDeep(
      documentTypeInitialValues[agency?.document_type ?? documentType] ??
        sharedInitialValues
    )

    values.document_type = agency?.document_type ?? documentType

    // Preserve tax_code, tax_code_description, and jurisdiction_type when documentType changes
    // (only in add modal, not when editing existing agency)
    // Only preserve if the field has a value (not empty string)
    if (!agency) {
      if (preservedFields.tax_code) {
        values.tax_code = preservedFields.tax_code
      }
      if (preservedFields.tax_code_description) {
        values.tax_code_description = preservedFields.tax_code_description
      }
      if (preservedFields.jurisdiction_type) {
        values.jurisdiction_type = preservedFields.jurisdiction_type
      }
    }

    if (agency) {
      let agencyData = cloneDeep({
        ...agency,
        mailing_preferences: {
          ...agency.mailing_preferences,
          due_date: {
            ...agency.mailing_preferences.due_date,
            start_date: getStartDate(
              agency.mailing_preferences?.due_date?.start_date
            ),
          },
        },
      })

      if (
        agency.document_type ===
        DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPE.DAILY_OR_MONTHLY_CHECKS_AND_COUPONS
      ) {
        agencyData = {
          ...agencyData,
          includeAlternateMailingAddress: Boolean(
            agency?.mailing_preferences?.alternate_mailing_address
          ),
          includeOvernightMailingAddress: Boolean(
            agency?.mailing_preferences?.overnight_mailing_address
          ),
        }

        const mailingAddressKeys = [
          'primary_mailing_address',
          'overnight_mailing_address',
          'alternate_mailing_address',
        ]
        mailingAddressKeys.forEach((key) => {
          if (!agencyData.mailing_preferences[key]) {
            set(agencyData, `mailing_preferences.${key}`, {
              ...documentTypeInitialValues[
                DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPE
                  .DAILY_OR_MONTHLY_CHECKS_AND_COUPONS
              ].mailing_preferences[key],
            })
          }
        })
      } else if (
        agency.document_type ===
        DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPE.QUARTER_END_RETURNS
      ) {
        const hasValidIdsIndividualCheck =
          agency?.mailing_preferences?.agency_package?.payment_types?.valid_ids
            ?.individual_check

        const hasValidIdsBatchCheck =
          agency?.mailing_preferences?.agency_package?.payment_types?.valid_ids
            ?.batch_check

        const hasAppliedForsIndividualCheck =
          agency?.mailing_preferences?.agency_package?.payment_types
            ?.applied_fors?.individual_check

        const hasAppliedForsBatchCheck =
          agency?.mailing_preferences?.agency_package?.payment_types
            ?.applied_fors?.batch_check

        agencyData = {
          ...agencyData,
          includeAgencyPackageReturn: Boolean(
            agency?.mailing_preferences?.agency_package?.return &&
              hasTrue(agency?.mailing_preferences?.agency_package?.return)
          ),
          includeAgencyPackageWageDetail: Boolean(
            agency?.mailing_preferences?.agency_package?.wage_detail &&
              hasTrue(agency?.mailing_preferences?.agency_package?.wage_detail)
          ),
          includeAgencyPackagePaymentTypes: Boolean(
            agency?.mailing_preferences?.agency_package?.payment_types &&
              hasTrue(
                agency?.mailing_preferences?.agency_package?.payment_types
              )
          ),
          includeAgencyPackageOtherRequirements: Boolean(
            agency?.mailing_preferences?.agency_package?.other_requirements &&
              hasTrue(
                agency?.mailing_preferences?.agency_package?.other_requirements
              )
          ),
          includeAddress1: Boolean(
            agency?.mailing_preferences?.agency_package?.mailing_information
              ?.address1
          ),
          includeAddress2: Boolean(
            agency?.mailing_preferences?.agency_package?.mailing_information
              ?.address2
          ),
          includeAddress3: Boolean(
            agency?.mailing_preferences?.agency_package?.mailing_information
              ?.address3
          ),
          includeAddress4: Boolean(
            agency?.mailing_preferences?.agency_package?.mailing_information
              ?.address4
          ),
          shouldSortDocumentsAddress1: Boolean(
            agency?.mailing_preferences?.agency_package?.mailing_information
              ?.address1?.sort_documents_sort_by
          ),
          shouldSortDocumentsAddress2: Boolean(
            agency?.mailing_preferences?.agency_package?.mailing_information
              ?.address2?.sort_documents_sort_by
          ),
          shouldSortDocumentsAddress3: Boolean(
            agency?.mailing_preferences?.agency_package?.mailing_information
              ?.address3?.sort_documents_sort_by
          ),
          shouldSortDocumentsAddress4: Boolean(
            agency?.mailing_preferences?.agency_package?.mailing_information
              ?.address4?.sort_documents_sort_by
          ),
        }

        // Handle valid_ids independently
        if (hasValidIdsIndividualCheck) {
          set(
            agencyData,
            'mailing_preferences.agency_package.payment_types.valid_ids.batch_check',
            false
          )
        } else if (hasValidIdsBatchCheck) {
          set(
            agencyData,
            'mailing_preferences.agency_package.payment_types.valid_ids.individual_check',
            false
          )
        }

        // Handle applied_fors independently
        if (hasAppliedForsIndividualCheck) {
          set(
            agencyData,
            'mailing_preferences.agency_package.payment_types.applied_fors.batch_check',
            false
          )
        } else if (hasAppliedForsBatchCheck) {
          set(
            agencyData,
            'mailing_preferences.agency_package.payment_types.applied_fors.individual_check',
            false
          )
        }

        const mailingInformationKey =
          'mailing_preferences.agency_package.mailing_information'
        const mailingInformation = get(agencyData, mailingInformationKey) || {}
        Object.entries(mailingInformation).forEach(([key, value]) => {
          if (!value || isEmptyObject(value)) {
            set(agencyData, `${mailingInformationKey}.${key}`, {
              ...documentTypeInitialValues[
                DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPE.QUARTER_END_RETURNS
              ].mailing_preferences.agency_package.mailing_information[key],
            })
          }
        })
      }

      values = mergeDeep(values, agencyData, true)
    }

    return values
  }, [agency, documentType, preservedFields])

  const saveAgencyPreference = async ({ values, updatedValues }) => {
    try {
      let message = `Tax Code ${agency?.tax_code} has been successfully updated!`

      if (isAddModal) {
        await addAgency({ values: updatedValues })
        message = `Tax Code ${values.tax_code} has been successfully added!`
      } else {
        const body = { values: {} }

        Object.entries(updatedValues).forEach(([k, v]) => {
          // Use deep comparison for nested objects and arrays, shallow for primitives
          const isDifferent =
            (typeof v === 'object' && v !== null) || Array.isArray(v)
              ? !isEqual(agency[k], v)
              : agency[k] !== v

          if (isDifferent) {
            body.values[k] = v
          }
        })

        await updateAgencyPreferences({
          agencyId: agency?.id,
          ...body,
        })
        message = `${agency?.tax_code} (${documentTypeDisplay}) preference has been successfully updated!`
      }

      setBasicNotification(message)
      setOpen(false)
    } catch (err) {
      setError(
        err.response?.data?.display_message ||
          err?.message ||
          'Unable to update agency preference.'
      )
    }
  }

  const handleSubmit = useCallback(
    async (_values) => {
      try {
        // Extract values that will not be stored in the database
        const {
          includeOvernightMailingAddress,
          includeAlternateMailingAddress,
          includeAddress1,
          includeAddress2,
          includeAddress3,
          includeAddress4,
          shouldSortDocumentsAddress1,
          shouldSortDocumentsAddress2,
          shouldSortDocumentsAddress3,
          shouldSortDocumentsAddress4,
          includeAgencyPackageReturn,
          includeAgencyPackageWageDetail,
          includeAgencyPackagePaymentTypes,
          includeAgencyPackageOtherRequirements,
          ...values
        } = _values

        const updatedValues = cloneDeep(values)

        /* Shared automatic validation */

        if (updatedValues.mailing_preferences.due_date.start_date) {
          updatedValues.mailing_preferences.due_date.start_date = format(
            updatedValues.mailing_preferences.due_date.start_date,
            'yyyy-MM-dd'
          )
        }

        /* Document type specific automatic validation */

        if (
          _values.document_type ===
          DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPE.QUARTER_END_RETURNS
        ) {
          if (!includeAgencyPackageReturn) {
            updatedValues.mailing_preferences.agency_package.return = null
          }

          if (!includeAgencyPackageWageDetail) {
            updatedValues.mailing_preferences.agency_package.wage_detail = null
          }

          if (!includeAgencyPackagePaymentTypes) {
            updatedValues.mailing_preferences.agency_package.payment_types =
              null
          }

          if (!includeAgencyPackageOtherRequirements) {
            updatedValues.mailing_preferences.agency_package.other_requirements =
              null
          }

          if (!shouldSortDocumentsAddress1) {
            set(
              updatedValues,
              'mailing_preferences.agency_package.mailing_information.address1.sort_documents_sort_by',
              ''
            )
          }

          if (!shouldSortDocumentsAddress2) {
            set(
              updatedValues,
              'mailing_preferences.agency_package.mailing_information.address2.sort_documents_sort_by',
              ''
            )
          }

          if (!shouldSortDocumentsAddress3) {
            set(
              updatedValues,
              'mailing_preferences.agency_package.mailing_information.address3.sort_documents_sort_by',
              ''
            )
          }

          if (!shouldSortDocumentsAddress4) {
            set(
              updatedValues,
              'mailing_preferences.agency_package.mailing_information.address4.sort_documents_sort_by',
              ''
            )
          }

          if (!includeAddress1) {
            set(
              updatedValues,
              'mailing_preferences.agency_package.mailing_information.address1',
              null
            )
          }

          if (!includeAddress2) {
            set(
              updatedValues,
              'mailing_preferences.agency_package.mailing_information.address2',
              null
            )
          }

          if (!includeAddress3) {
            set(
              updatedValues,
              'mailing_preferences.agency_package.mailing_information.address3',
              null
            )
          }

          if (!includeAddress4) {
            set(
              updatedValues,
              'mailing_preferences.agency_package.mailing_information.address4',
              null
            )
          }

          if (
            !updatedValues.mailing_preferences.agency_package?.return?.valid_ids
              ?.efile_method
          ) {
            set(
              updatedValues,
              'mailing_preferences.agency_package.return.valid_ids.efile_method',
              ''
            )
          }

          if (
            !updatedValues.mailing_preferences.agency_package?.return
              ?.applied_fors?.efile_method
          ) {
            set(
              updatedValues,
              'mailing_preferences.agency_package.return.applied_fors.efile_method',
              ''
            )
          }

          if (
            !updatedValues.mailing_preferences.agency_package?.wage_detail
              ?.valid_ids?.efile_method
          ) {
            set(
              updatedValues,
              'mailing_preferences.agency_package.wage_detail.valid_ids.efile_method',
              ''
            )
          }

          if (
            !updatedValues.mailing_preferences.agency_package?.wage_detail
              ?.applied_fors?.efile_method
          ) {
            set(
              updatedValues,
              'mailing_preferences.agency_package.wage_detail.applied_fors.efile_method',
              ''
            )
          }

          const includedAddresses = [
            includeAddress1 && 'address1',
            includeAddress2 && 'address2',
            includeAddress3 && 'address3',
            includeAddress4 && 'address4',
          ].filter(Boolean)

          if (includedAddresses.length > 0) {
            const basePath =
              'mailing_preferences.agency_package.mailing_information'
            const hasAddressChanges = includedAddresses.some(
              (addr) =>
                !deepEqual(
                  get(agency ?? {}, `${basePath}.${addr}`) ?? {},
                  get(updatedValues, `${basePath}.${addr}`) ?? {}
                )
            )

            if (hasAddressChanges) {
              const { modalProps, component } = await generateAddressModalProps(
                {
                  values: Object.entries(
                    updatedValues.mailing_preferences.agency_package
                      .mailing_information ?? {}
                  )
                    .filter(([_, value]) => value)
                    .map(([key, value]) => ({ ...value, key })),
                  onContinue: (updatedAddressValues) => {
                    updatedAddressValues.forEach((address) => {
                      set(
                        updatedValues,
                        `mailing_preferences.agency_package.mailing_information.${address.key}`,
                        address
                      )
                    })
                    saveAgencyPreference({ values, updatedValues }).finally(
                      () => {
                        setModalOpen(false)
                      }
                    )
                  },
                  onCancel: () => setModalOpen(false),
                  addressKeys: {
                    addressname: 'key',
                    address: 'address_line_1',
                    addressalt: 'address_line_2',
                    city: 'city',
                    state: 'state',
                    zip: 'zip',
                    country: 'country',
                  },
                }
              )

              if (component) {
                setModalComponent(component)
              }

              setModalProps(modalProps)
              setModalOpen(true)
            } else {
              saveAgencyPreference({ values, updatedValues })
            }
          } else {
            saveAgencyPreference({ values, updatedValues })
          }
        } else {
          if (!includeOvernightMailingAddress) {
            updatedValues.mailing_preferences.overnight_mailing_address = null
            updatedValues.mailing_preferences.always_ship_overnight = false
          }

          if (!includeAlternateMailingAddress) {
            updatedValues.mailing_preferences.alternate_mailing_address = null
          }

          if (!updatedValues.mailing_preferences.applied_fors?.send) {
            set(
              updatedValues,
              'mailing_preferences.applied_fors.mailing_address',
              ''
            )
            set(
              updatedValues,
              'mailing_preferences.applied_fors.mail_with_checks',
              true
            )
          }

          if (!updatedValues.mailing_preferences.reports?.generate_reports) {
            set(
              updatedValues,
              'mailing_preferences.reports.mailing_address',
              ''
            )
            set(
              updatedValues,
              'mailing_preferences.reports.mail_with_checks',
              true
            )
          }

          if (!updatedValues.mailing_preferences.cd?.generate_cd) {
            set(updatedValues, 'mailing_preferences.cd.mailing_address', '')
            set(updatedValues, 'mailing_preferences.cd.mail_with_checks', true)
          }

          if (!updatedValues.mailing_preferences.special_handling?.required) {
            set(
              updatedValues,
              'mailing_preferences.special_handling.instructions',
              ''
            )
          }

          const includedAddresses = [
            'primary_mailing_address',
            'overnight_mailing_address',
            'alternate_mailing_address',
          ].filter(Boolean)

          if (includedAddresses.length > 0) {
            const basePath = 'mailing_preferences'
            const hasAddressChanges = includedAddresses.some(
              (addr) =>
                !deepEqual(
                  get(agency ?? {}, `${basePath}.${addr}`) ?? {},
                  get(updatedValues, `${basePath}.${addr}`) ?? {}
                )
            )

            if (hasAddressChanges) {
              const { modalProps, component } = await generateAddressModalProps(
                {
                  values: Object.entries(
                    updatedValues.mailing_preferences ?? {}
                  )
                    .filter(
                      ([key, value]) => value && includedAddresses.includes(key)
                    )
                    .map(([key, value]) => ({ ...value, key })),
                  onContinue: (updatedAddressValues) => {
                    updatedAddressValues.forEach((address) => {
                      set(
                        updatedValues,
                        `mailing_preferences.${address.key}`,
                        address
                      )
                    })
                    saveAgencyPreference({ values, updatedValues }).finally(
                      () => {
                        setModalOpen(false)
                      }
                    )
                  },
                  onCancel: () => setModalOpen(false),
                  addressKeys: {
                    addressname: 'key',
                    address: 'address1',
                    addressalt: 'address2',
                    city: 'city',
                    state: 'state',
                    zip: 'zip',
                    country: 'country',
                  },
                }
              )

              if (component) {
                setModalComponent(component)
              }

              setModalProps(modalProps)
              setModalOpen(true)
            } else {
              saveAgencyPreference({ values, updatedValues })
            }
          } else {
            saveAgencyPreference({ values, updatedValues })
          }
        }
      } catch (err) {
        setError(
          err.response?.data?.display_message ||
            err?.message ||
            'Unable to update agency preference.'
        )
      }
    },
    [
      agency,
      updateAgencyPreferences,
      addAgency,
      setOpen,
      isAddModal,
      setBasicNotification,
    ]
  )

  const validate = useCallback((values) => {
    const errors = {}

    if (
      values.document_type ===
      DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPE.QUARTER_END_RETURNS
    ) {
      // Validate mailing address selections
      Array.from({ length: 4 }).forEach((_, index) => {
        if (get(values, `includeAddress${index + 1}`)) {
          const address = get(
            values,
            `mailing_preferences.agency_package.mailing_information.address${
              index + 1
            }`
          )

          if (!address?.media_type) {
            errors[
              `mailing_preferences.agency_package.mailing_information.address${
                index + 1
              }.media_type`
            ] = 'Media type is required'
          }

          if (!address?.attach_order) {
            errors[
              `mailing_preferences.agency_package.mailing_information.address${
                index + 1
              }.attach_order`
            ] = 'Attach order is required'
          } else if (
            !Object.keys(DAYFORCE_AGENCY_PREFERENCES_ATTACH_ORDER).includes(
              address?.attach_order
            )
          ) {
            errors[
              `mailing_preferences.agency_package.mailing_information.address${
                index + 1
              }.attach_order`
            ] = 'Invalid attach order selection'
          }

          if (address?.sort_documents_sort_by) {
            if (
              !Object.keys(
                DAYFORCE_AGENCY_PREFERENCES_DOCUMENTS_SORT_BY
              ).includes(address?.sort_documents_sort_by)
            ) {
              errors[
                `mailing_preferences.agency_package.mailing_information.address${
                  index + 1
                }.sort_documents_sort_by`
              ] = 'Invalid sort documents sort by selection'
            }
          }

          if (!address?.mail_method) {
            errors[
              `mailing_preferences.agency_package.mailing_information.address${
                index + 1
              }.mail_method`
            ] = 'Mail method is required'
          }

          if (!address?.address_line_1) {
            errors[
              `mailing_preferences.agency_package.mailing_information.address${
                index + 1
              }.address_line_1`
            ] = 'Address Line 1 is required'
          }

          if (!address?.city) {
            errors[
              `mailing_preferences.agency_package.mailing_information.address${
                index + 1
              }.city`
            ] = 'City is required'
          }

          if (!address?.state) {
            errors[
              `mailing_preferences.agency_package.mailing_information.address${
                index + 1
              }.state`
            ] = 'State is required'
          }

          if (!address?.zip) {
            errors[
              `mailing_preferences.agency_package.mailing_information.address${
                index + 1
              }.zip`
            ] = 'Zip code is required'
          }

          if (!address?.country) {
            errors[
              `mailing_preferences.agency_package.mailing_information.address${
                index + 1
              }.country`
            ] = 'Country is required'
          }
        }
      })
    } else {
      if (values.includeOvernightMailingAddress) {
        const overnightAddress =
          values.mailing_preferences?.overnight_mailing_address
        if (!overnightAddress?.address1)
          errors['mailing_preferences.overnight_mailing_address.address1'] =
            'Address is required'
        if (!overnightAddress?.recipient_line_1)
          errors[
            'mailing_preferences.overnight_mailing_address.recipient_line_1'
          ] = 'Recipient line 1 is required'
        if (!overnightAddress?.city)
          errors['mailing_preferences.overnight_mailing_address.city'] =
            'City is required'
        if (!overnightAddress?.state)
          errors['mailing_preferences.overnight_mailing_address.state'] =
            'State is required'
        if (!overnightAddress?.zip)
          errors['mailing_preferences.overnight_mailing_address.zip'] =
            'Zip code is required'
        if (!overnightAddress?.country)
          errors['mailing_preferences.overnight_mailing_address.country'] =
            'Country is required'
      }

      if (values.includeAlternateMailingAddress) {
        const alternateAddress =
          values.mailing_preferences?.alternate_mailing_address
        if (!alternateAddress?.address1)
          errors['mailing_preferences.alternate_mailing_address.address1'] =
            'Address is required'
        if (!alternateAddress?.recipient_line_1)
          errors[
            'mailing_preferences.alternate_mailing_address.recipient_line_1'
          ] = 'Recipient line 1 is required'
        if (!alternateAddress?.city)
          errors['mailing_preferences.alternate_mailing_address.city'] =
            'City is required'
        if (!alternateAddress?.state)
          errors['mailing_preferences.alternate_mailing_address.state'] =
            'State is required'
        if (!alternateAddress?.zip)
          errors['mailing_preferences.alternate_mailing_address.zip'] =
            'Zip code is required'
        if (!alternateAddress?.country)
          errors['mailing_preferences.alternate_mailing_address.country'] =
            'Country is required'
      }

      // Validate mailing address selections
      const validateMailingAddressSelection = (path) => {
        const selectedAddress = get(values, path)
        if (
          selectedAddress === 'overnight_mailing_address' &&
          !values.includeOvernightMailingAddress
        ) {
          errors[path] = 'Overnight mailing address is not enabled'
        }
        if (
          selectedAddress === 'alternate_mailing_address' &&
          !values.includeAlternateMailingAddress
        ) {
          errors[path] = 'Alternate mailing address is not enabled'
        }
      }

      // Check all mailing address selections
      validateMailingAddressSelection(
        'mailing_preferences.applied_fors.mailing_address'
      )
      validateMailingAddressSelection(
        'mailing_preferences.reports.mailing_address'
      )
      validateMailingAddressSelection('mailing_preferences.cd.mailing_address')

      if (
        values.mailing_preferences.applied_fors?.send &&
        !values.mailing_preferences.applied_fors?.mail_with_checks &&
        !values.mailing_preferences.applied_fors?.mailing_address
      ) {
        errors['mailing_preferences.applied_fors.mailing_address'] =
          'Mailing address is required'
      }

      if (
        values.mailing_preferences.reports?.generate_reports &&
        !values.mailing_preferences.reports?.mail_with_checks &&
        !values.mailing_preferences.reports?.mailing_address
      ) {
        errors['mailing_preferences.reports.mailing_address'] =
          'Mailing address is required'
      }

      if (
        values.mailing_preferences.cd?.generate_cd &&
        !values.mailing_preferences.cd?.mail_with_checks &&
        !values.mailing_preferences.cd?.mailing_address
      ) {
        errors['mailing_preferences.cd.mailing_address'] =
          'Mailing address is required'
      }

      if (
        values.mailing_preferences.special_handling?.required &&
        !values.mailing_preferences.special_handling?.instructions
      ) {
        errors['mailing_preferences.special_handling.instructions'] =
          'Special handling instructions are required'
      }
    }

    return errors
  }, [])

  const footerButtonProps = useMemo(() => {
    const isTaxCodeExistsError =
      formikRef.current?.errors?.tax_code === 'Preference already exists'
    return [
      {
        children: <Box>Save Preference</Box>,
        variant: 'contained',
        color: 'primary',
        disabled: isTaxCodeExistsError,
        onClick: () => {
          formikRef.current.submitForm()
        },
      },
      {
        children: 'Cancel',
        variant: 'outlined',
        color: 'primary',
        onClick: () => setOpen(false),
        popover: true,
        popoverMessage: (
          <>
            Are you sure you want to cancel?
            <br /> All changes will be lost.
          </>
        ),
        popoverProps: {
          anchorOrigin: {
            vertical: -108,
            horizontal: -64,
          },
        },
        cancelButtonProps: {
          children: 'No, Keep Editing',
        },
        acceptButtonProps: {
          children: 'Confirm Cancel',
        },
      },
    ]
  }, [formikRef, setOpen])

  const validationSchema = useMemo(() => {
    return Yup.object().shape(
      documentTypeValidationSchema[documentType] ?? sharedValidationSchema
    )
  }, [documentType])

  useEffect(() => {
    if (agency && open) {
      setDocumentType(agency?.document_type || '')
    }
  }, [agency, open])

  useEffect(() => {
    blockIdleTimer(open)

    if (!open) {
      setDocumentType('')
      setPreservedFields({
        tax_code: '',
        tax_code_description: '',
        jurisdiction_type: '',
      })
    }
  }, [open])

  return (
    <Modal
      open={open}
      onClose={() => {}}
      height="90vh"
      width="732px"
      title={isAddModal ? 'Add Agency Preference' : 'Edit Agency Preference'}
      subTitle={
        isAddModal ? (
          ''
        ) : (
          <>
            <Box sx={{ display: 'flex' }}>
              Tax Code {agency?.tax_code}{' '}
              {Boolean(documentTypeDisplay) && (
                <Typography variant="h3" fontWeight="medium" my="auto" ml={1}>
                  ({documentTypeDisplay})
                </Typography>
              )}
            </Box>
          </>
        )
      }
      scrollable
      footerButtonProps={footerButtonProps}
      pt={4}
      titleProps={{
        titleTypographyProps: {
          variant: 'h1',
          sx: {
            textAlign: 'center',
            fontSize: 32,
            mb: 5,
          },
        },
        subheaderTypographyProps: {
          variant: 'h2',
          color: 'textPrimary',
          sx: {
            fontSize: 24,
            fontWeight: 'bold',
            mb: 5,
          },
        },
      }}
    >
      <Formik
        enableReinitialize
        innerRef={formikRef}
        onSubmit={handleSubmit}
        validate={validate}
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ handleChange, values, errors, touched }) => {
          const isTaxCodeExistsError =
            errors?.tax_code === 'Tax code already exists'
          const formDisabled = isTaxCodeExistsError
          const isDocumentTypeSelected = Boolean(values?.document_type)

          return (
            <Box sx={{ pb: 12, pt: 2 }}>
              <Stack spacing={3}>
                <TaxCodeInput isAddModal={isAddModal} />
                <TextField
                  required
                  fullWidth
                  size="small"
                  label="Tax Code Description"
                  value={values?.tax_code_description || ''}
                  error={Boolean(
                    get(touched, 'tax_code_description') &&
                      get(errors, 'tax_code_description')
                  )}
                  helperText={
                    get(touched, 'tax_code_description') &&
                    get(errors, 'tax_code_description')
                  }
                  onChange={handleChange}
                  name="tax_code_description"
                  variant="outlined"
                  disabled={formDisabled}
                />
                <TextField
                  required
                  value={values?.jurisdiction_type || ''}
                  onChange={handleChange}
                  name="jurisdiction_type"
                  label="Jurisdiction Type"
                  select
                  fullWidth
                  size="small"
                  error={Boolean(
                    get(touched, 'jurisdiction_type') &&
                      get(errors, 'jurisdiction_type')
                  )}
                  helperText={
                    get(touched, 'jurisdiction_type') &&
                    get(errors, 'jurisdiction_type')
                  }
                  variant="outlined"
                  disabled={formDisabled}
                >
                  {Object.entries(
                    DAYFORCE_AGENCY_PREFERENCES_JURISDICTION_TYPES
                  ).map(([value, display]) => (
                    <MenuItem key={value} value={value}>
                      {display}
                    </MenuItem>
                  ))}
                </TextField>
                <Typography variant="h1" sx={{ mb: 4 }}>
                  Document Configuration
                </Typography>
                <DocumentTypeSelect
                  isAddModal={isAddModal}
                  setDocumentType={setDocumentType}
                  formDisabled={formDisabled}
                  setPreservedFields={setPreservedFields}
                />
                {isDocumentTypeSelected && (
                  <>
                    <DueDateSelect disabled={formDisabled} />
                    {forms[values?.document_type || documentType] ?? <></>}
                  </>
                )}
              </Stack>
            </Box>
          )
        }}
      </Formik>
    </Modal>
  )
}

export default DayforceEditPreferencesModal
